import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyToken } from '../lib/jwtUtils';
import api from '../lib/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeRole, setActiveRole] = useState(null); // For role switching
  const navigate = useNavigate();
  const location = useLocation();

  // Check for existing auth token on app load
  useEffect(() => {
    const validateAndSetAuth = async () => {
      console.log('AuthContext: Validating authentication...');
      const token = localStorage.getItem('authToken');
      const currentPath = location.pathname;

      if (token) {
        try {
          // Verify token is valid and not expired
          const payload = verifyToken(token);

          if (payload) {
            console.log('AuthContext: Token is valid, fetching user profile');

            // Fetch user profile to ensure user still exists and is active
            const response = await api.get('/auth/me');
            const userData = response.data;

            // Check if user has SUPER_ADMIN or content roles
            const roles = userData.roles || [];
            const isSuperAdmin = roles.includes('ROLE_SUPER_ADMIN') || roles.includes('SUPER_ADMIN');
            const isAnalyst = roles.includes('ROLE_ANALYST') || roles.includes('ANALYST');
            const isContentSupervisor = roles.includes('ROLE_CONTENT_SUPERVISOR') || roles.includes('CONTENT_SUPERVISOR');
            const isContentGenerator = roles.includes('ROLE_CONTENT_GENERATOR') || roles.includes('CONTENT_GENERATOR');
            const isContentReviewer = roles.includes('ROLE_CONTENT_REVIEWER') || roles.includes('CONTENT_REVIEWER');

            if (isSuperAdmin || isAnalyst || isContentSupervisor || isContentGenerator || isContentReviewer) {
              let userRole = 'SUPER_ADMIN';
              if (isContentSupervisor) userRole = 'CONTENT_SUPERVISOR';
              else if (isContentGenerator) userRole = 'CONTENT_GENERATOR';
              else if (isContentReviewer) userRole = 'CONTENT_REVIEWER';
              else if (isAnalyst) userRole = 'ANALYST';

              console.log(`AuthContext: User has ${userRole} role, setting authenticated`);

              // For Super Admin, check if there's a stored active role
              let currentActiveRole = userRole;
              if (isSuperAdmin) {
                const storedActiveRole = localStorage.getItem('activeRole');
                if (storedActiveRole && ['SUPER_ADMIN', 'CONTENT_SUPERVISOR', 'CONTENT_GENERATOR', 'CONTENT_REVIEWER'].includes(storedActiveRole)) {
                  currentActiveRole = storedActiveRole;
                }
              }

              setIsAuthenticated(true);
              setUser({
                id: userData.id || payload.sub || payload.userId,
                name: userData.name || localStorage.getItem('userName'),
                email: userData.email,
                role: userRole, // Original role
                roles: roles,
                token: token,
                isSuperAdmin: isSuperAdmin
              });
              setActiveRole(currentActiveRole);

              // If we're on the login page, redirect to dashboard
              if (currentPath === '/login') {
                console.log('AuthContext: Already logged in, redirecting to dashboard');
                setTimeout(() => {
                  navigate('/', { replace: true });
                }, 100);
              }

              setIsLoading(false);
              return;
            } else {
              console.log('AuthContext: User does not have required role, clearing auth');
              // User doesn't have required role, clear auth
              localStorage.removeItem('authToken');
              localStorage.removeItem('userRole');
              localStorage.removeItem('userName');
              localStorage.removeItem('userEmail');
            }
          } else {
            console.error('AuthContext: Token is expired or invalid');

            // Clear the invalid token
            localStorage.removeItem('authToken');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userName');
            localStorage.removeItem('userEmail');

            // If we're not on a public page, redirect to login
            const publicRoutes = ['/login', '/otp-verification'];
            if (!publicRoutes.includes(currentPath)) {
              console.log('AuthContext: Token invalid, redirecting to login');
              navigate('/login', { replace: true });
            }

            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('AuthContext: Token validation error:', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userName');
          localStorage.removeItem('userEmail');

          // Show user-friendly error message for service unavailability
          if (!error.response) {
            console.error('AuthContext: Authentication service unavailable');
            // Don't redirect to login if service is down, let user stay on current page
            // They'll see the error when they try to use authenticated features
          }
        }
      } else {
        console.log('AuthContext: No token found');
      }

      setIsLoading(false);
    };

    validateAndSetAuth();
  }, [location.pathname, navigate]);

  const login = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('isFirstTimeLogin');
    localStorage.removeItem('activeRole');
    setIsAuthenticated(false);
    setUser(null);
    setActiveRole(null);
  };

  const switchRole = (newRole) => {
    if (user?.isSuperAdmin && ['SUPER_ADMIN', 'CONTENT_SUPERVISOR', 'CONTENT_GENERATOR', 'CONTENT_REVIEWER'].includes(newRole)) {
      setActiveRole(newRole);
      localStorage.setItem('activeRole', newRole);
      console.log(`AuthContext: Switched to role ${newRole}`);
    }
  };

  const value = {
    isAuthenticated,
    user,
    isLoading,
    activeRole,
    login,
    logout,
    switchRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
